const STORAGE_KEY = 'menu-recently-opened'
const MAX_ITEMS = 5

// Dedup by what the user sees (section/group/label), not by href: the same page
// carries a different href per organization, which would otherwise show up as
// visually identical duplicate rows for admins who switch orgs/accounts.
function recentKey(item) {
  return `${item.section ?? ''}|${item.groupLabel ?? ''}|${item.label}`
}

export function readRecentlyOpened() {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY))
    if (!Array.isArray(parsed)) return []
    return parsed.filter((i) => i && typeof i.href === 'string' && typeof i.label === 'string').slice(0, MAX_ITEMS)
  } catch {
    return []
  }
}

export function recordRecentlyOpened(item) {
  if (!item?.href || !item.label) return readRecentlyOpened()
  const entry = {
    label: item.label,
    href: item.href,
    groupLabel: item.groupLabel ?? null,
    section: item.section ?? null,
    icon: item.icon ?? null,
  }
  const key = recentKey(entry)
  const current = readRecentlyOpened()
  // identical entry already at the front — return the same reference so React bails out of the re-render
  if (current[0] && current[0].href === entry.href && recentKey(current[0]) === key) return current
  // drop any prior entry that is the same page (href) OR renders identically (section/group/label):
  // catches both the cross-org case (same page, different href) and menu-restructure drift
  // (same href, different section/group), so a page never appears twice.
  const next = [entry, ...current.filter((i) => i.href !== entry.href && recentKey(i) !== key)].slice(0, MAX_ITEMS)
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    // best-effort: private mode / quota exceeded
  }
  return next
}

export function clearRecentlyOpened() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // best-effort: private mode
  }
  return []
}

export function findActiveLeaf(menu) {
  if (!menu) return null
  for (const provider of menu.providers ?? []) {
    for (const section of menu.provider_menus?.[provider] ?? []) {
      for (const entry of section.entries ?? []) {
        if (entry.type === 'link' && entry.active) {
          return { label: entry.label, href: entry.href, groupLabel: null, section: section.label, icon: entry.icon }
        }
        if (entry.type === 'group') {
          for (const item of entry.items ?? []) {
            if (item.active) {
              return {
                label: item.label,
                href: item.href,
                groupLabel: entry.label,
                section: section.label,
                icon: entry.icon,
              }
            }
          }
        }
      }
    }
  }
  for (const group of menu.admin?.groups ?? []) {
    for (const item of group.items ?? []) {
      // admin breadcrumb reads "Admin › <subgroup>": section is the top-level Admin label,
      // group is the subgroup the item lives in
      if (item.active) {
        return {
          label: item.label,
          href: item.href,
          groupLabel: group.label,
          section: menu.admin.label,
          icon: group.icon ?? menu.admin.icon,
        }
      }
    }
  }
  return null
}
